// FilterPanel.jsx — property_type options dynamic from backend propertyTypes
import React, { useState, useRef, useCallback, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './FilterPanel.css';
import { MyContext } from '../../../../Context/MyContext';
import AppIcon from '../../../../Components/AppIcon';
import SearchSuggest from '../../../../Components/SearchSuggest';

const fmtPrice = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

// ── Dual Range Slider ──────────────────────────────────────────
const DualSlider = ({ min, max, minVal, maxVal, onMinChange, onMaxChange, format }) => {
  const trackRef   = useRef(null);
  const dragging   = useRef(null);
  const currentMin = minVal ?? min;
  const currentMax = maxVal ?? max;
  const pct = (v) => ((v - min) / (max - min)) * 100;

  const getValFromEvent = useCallback((e) => {
    const rect    = trackRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const step    = (max - min) / 100;
    return Math.round((min + ratio * (max - min)) / step) * step;
  }, [min, max]);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const val = getValFromEvent(e);
    if (dragging.current === 'min') onMinChange(Math.max(min, Math.min(val, currentMax - (max-min)/100)));
    else                            onMaxChange(Math.min(max, Math.max(val, currentMin + (max-min)/100)));
  }, [dragging, getValFromEvent, currentMin, currentMax, min, max, onMinChange, onMaxChange]);

  const onUp = useCallback(() => { dragging.current = null; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [onMove, onUp]);

  return (
    <div className="ds-wrap">
      <div className="ds-labels">
        <span>{format ? format(currentMin) : currentMin}</span>
        <span>{format ? format(currentMax) : currentMax}</span>
      </div>
      <div className="ds-track" ref={trackRef}>
        <div className="ds-fill" style={{ left: `${pct(currentMin)}%`, width: `${pct(currentMax)-pct(currentMin)}%` }}/>
        <div className="ds-thumb" style={{ left: `${pct(currentMin)}%` }}
          onMouseDown={e => { e.preventDefault(); dragging.current = 'min'; }}
          onTouchStart={() => { dragging.current = 'min'; }}/>
        <div className="ds-thumb" style={{ left: `${pct(currentMax)}%` }}
          onMouseDown={e => { e.preventDefault(); dragging.current = 'max'; }}
          onTouchStart={() => { dragging.current = 'max'; }}/>
      </div>
    </div>
  );
};

// ── Multi-Select Dropdown ──────────────────────────────────────
const MultiDrop = ({ placeholder, options, values = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const norm = (v) => String(v);
  const toggle = (val) => {
    const key = norm(val);
    const cur = (values || []).map(norm);
    onChange(cur.includes(key) ? values.filter((v) => norm(v) !== key) : [...values, val]);
  };
  const label = values.length === 0 ? placeholder
    : values.length === 1 ? (options.find(o => norm(o.value) === norm(values[0]))?.label || values[0])
    : `${values.length} selected`;

  return (
    <div className="md-wrap" ref={ref}>
      <button className={`md-trigger ${open?'open':''} ${values.length?'has-value':''}`} onClick={() => setOpen(o=>!o)}>
        <span className="md-trigger-text">{label}</span>
        <svg className={`md-arrow ${open?'open':''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="md-dropdown">
          {options.map(opt => (
            <label key={opt.value} className="md-item">
              <input type="checkbox" checked={(values || []).map(String).includes(String(opt.value))} onChange={() => toggle(opt.value)} className="md-cb"/>
              <span className="md-item-label">{opt.label}</span>
              {(values || []).map(String).includes(String(opt.value)) && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12" className="md-check-ico">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </label>
          ))}
          {values.length > 0 && (
            <button className="md-clear-all" onClick={() => { onChange([]); setOpen(false); }}>Clear</button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Radio Pills ────────────────────────────────────────────────
const RadioPills = ({ options, value, onChange }) => (
  <div className="fp-pills">
    {options.map(opt => (
      <button key={opt.value} className={`fp-pill ${value===opt.value?'active':''}`} onClick={() => onChange(opt.value)}>
        {opt.label}
      </button>
    ))}
  </div>
);

// ── Native Dropdown ────────────────────────────────────────────
const NativeDrop = ({ value, options, onChange, placeholder }) => (
  <div className="nd-wrap">
    <select className={`nd-select ${value?'has-value':''}`} value={value||''} onChange={e => onChange(e.target.value||null)}>
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <svg className="nd-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  </div>
);

// ── Section (collapsible) ──────────────────────────────────────
const Section = ({ label, children, badge }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="fp-section">
      <button className="fp-sec-head" onClick={() => setOpen(o=>!o)}>
        <span className="fp-sec-title">
          {label}
          {badge > 0 && <span className="fp-sec-badge">{badge}</span>}
        </span>
        <svg className={`fp-chevron ${open?'open':''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && <div className="fp-sec-body">{children}</div>}
    </div>
  );
};

// ── Main FilterPanel ───────────────────────────────────────────
const FilterPanel = ({
  filters,
  onChange,
  onClear,
  mobileOpen,
  onMobileClose,
  desktopVisible = true,
  desktopWrapperClassName = '',
  desktopCollapsed = false,
}) => {
  const navigate = useNavigate();
  const {
    filterConfig, categorys, states, cities, aminities,
    propertyTypes,  // ✅ dynamic from backend
  } = useContext(MyContext);

  // ── Resolve "from_property_types" → dynamic options ─────────
  // ✅ Build type_category_map from categorys dynamically
  // categorys[].type_id = [1, 2] — which type_ids this category belongs to
  const typeCategoryMap = useMemo(() => {
    const map = {};
    if (!Array.isArray(propertyTypes) || !Array.isArray(categorys)) return map;
    propertyTypes.forEach(pt => {
      const typeKey = pt.type_name.toLowerCase();
      // Find all category ids where this type_id is in category.type_id array
      const catIds = categorys
        .filter(c => Array.isArray(c.type_id) ? c.type_id.includes(pt.id) : c.type_id === pt.id)
        .map(c => c.id);
      if (catIds.length > 0) map[typeKey] = catIds;
    });
    return map;
  }, [propertyTypes, categorys]);

  // ✅ Build property_type options from backend propertyTypes
  const propertyTypeOptions = useMemo(() => {
    const opts = [{ value: 'all', label: 'All' }];
    if (Array.isArray(propertyTypes)) {
      propertyTypes.forEach(pt => {
        opts.push({
          value: pt.type_name.toLowerCase(),
          label: pt.type_name,
        });
      });
    }
    return opts;
  }, [propertyTypes]);

  // Resolve dynamic option lists
  const stateOptions   = states.map(s => ({ value: String(s.id), label: s.state_name }));
  const cityOptions    = cities
    .filter(c => !filters.state_id || String(c.state_id) === String(filters.state_id))
    .map(c => ({ value: String(c.id), label: c.city_name }));
  const aminityOptions = aminities.map(a => ({ value: String(a.id), label: a.aminitie_name }));

  // ✅ Category filtered by property_type cascade — uses dynamic typeCategoryMap
  const selectedTypeCfg   = filterConfig.find(c => c.key === 'property_type');
  const allowedCatIds     = filters.property_type && filters.property_type !== 'all'
    ? (typeCategoryMap[filters.property_type] ||
       selectedTypeCfg?.type_category_map?.[filters.property_type] || [])
    : null;
  const categoryOptions = categorys
    .filter(c => !allowedCatIds || allowedCatIds.includes(c.id))
    .map(c => ({ value: String(c.id), label: c.category_name }));

  // Visibility check
  const shouldShow = (cfg) => {
    if (!cfg.is_show) return false;
    if (cfg.show_for_kind?.length > 0 && !cfg.show_for_kind.includes(filters.listing_kind || 'all')) return false;
    if (cfg.show_for_category?.length > 0) {
      const selCats = (filters.category_id || []).map(String);
      if (!selCats.some(id => cfg.show_for_category.map(String).includes(id))) return false;
    }
    return true;
  };

  // ✅ Resolve options — handles "from_property_types"
  const resolveOptions = (cfg) => {
    if (cfg.options === 'from_categorys')      return categoryOptions;
    if (cfg.options === 'from_states')         return stateOptions;
    if (cfg.options === 'from_cities')         return cityOptions;
    if (cfg.options === 'from_aminities')      return aminityOptions;
    if (cfg.options === 'from_property_types') return propertyTypeOptions; // ✅ dynamic
    return Array.isArray(cfg.options) ? cfg.options : [];
  };

  const countActive = (cfg) => {
    if (cfg.type === 'radio')        return (filters[cfg.key] && filters[cfg.key] !== (cfg.default||'all')) ? 1 : 0;
    if (cfg.type === 'multi_drop')   return (filters[cfg.key] || []).length;
    if (cfg.type === 'range_slider') return (filters[cfg.url_key_min] != null || filters[cfg.url_key_max] != null) ? 1 : 0;
    if (cfg.type === 'dropdown')     return filters[cfg.key] ? 1 : 0;
    return 0;
  };

  const totalActive = filterConfig.reduce((a, c) => a + countActive(c), 0);

  const renderFilter = (cfg) => {
    if (!shouldShow(cfg)) return null;
    const opts = resolveOptions(cfg);

    if (cfg.type === 'radio') {
      // ✅ For property_type, use dynamic opts (not cfg.options which may be string)
      const radioOpts = cfg.options === 'from_property_types' ? propertyTypeOptions : (Array.isArray(cfg.options) ? cfg.options : []);
      return (
        <Section key={cfg.id} label={cfg.label} badge={countActive(cfg)}>
          <RadioPills
            options={radioOpts}
            value={filters[cfg.key] || cfg.default || 'all'}
            onChange={val => {
              if (cfg.key === 'listing_kind' && val === 'project') {
                onChange({ listing_kind: val, listing_type: 'all', property_type: 'all' });
              } else if (cfg.key === 'property_type') {
                // ✅ cascade: clear category when type changes
                onChange({ property_type: val, category_id: [] });
              } else {
                onChange({ [cfg.key]: val });
              }
            }}
          />
        </Section>
      );
    }

    if (cfg.type === 'multi_drop') {
      return (
        <Section key={cfg.id} label={cfg.label} badge={countActive(cfg)}>
          <MultiDrop
            placeholder={cfg.placeholder || `Select ${cfg.label}`}
            options={opts}
            values={filters[cfg.key] || []}
            onChange={vals => onChange({ [cfg.key]: vals })}
          />
        </Section>
      );
    }

    if (cfg.type === 'range_slider') {
      return (
        <Section key={cfg.id} label={cfg.label} badge={countActive(cfg)}>
          <DualSlider
            min={cfg.min} max={cfg.max}
            minVal={filters[cfg.url_key_min]}
            maxVal={filters[cfg.url_key_max]}
            onMinChange={v => onChange({ [cfg.url_key_min]: v === cfg.min ? null : v })}
            onMaxChange={v => onChange({ [cfg.url_key_max]: v === cfg.max ? null : v })}
            format={cfg.key === 'price' ? fmtPrice : null}
          />
        </Section>
      );
    }

    if (cfg.type === 'dropdown') {
      return (
        <Section key={cfg.id} label={cfg.label} badge={countActive(cfg)}>
          <NativeDrop
            value={filters[cfg.key]}
            options={opts}
            onChange={val => {
              if (cfg.key === 'state_id') onChange({ state_id: val, city_id: null });
              else onChange({ [cfg.key]: val });
            }}
            placeholder={`All ${cfg.label}s`}
          />
        </Section>
      );
    }

    return null;
  };

  const panelContent = (
    <div className="fp-inner">
      <div className="fp-header">
        <span className="fp-title">
          Filters
          {totalActive > 0 && <span className="fp-count-badge">{totalActive}</span>}
        </span>
        {totalActive > 0 && <button className="fp-clear" onClick={onClear}>Clear All</button>}
      </div>

      <div className="fp-search-wrap">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <SearchSuggest
          value={filters.search || ''}
          onChange={(search) => onChange({ search, _searchTyping: true })}
          onSubmit={(search, item) => {
            const companyId = item?.type === 'company' ? Number(item.id || item.company_id) : null;
            if (Number.isFinite(companyId) && companyId > 0) {
              navigate(`/company/${companyId}`);
              return;
            }
            onChange({ search, _searchCommit: true });
          }}
          placeholder="Search location, company, type, #property no…"
          inputClassName="fp-search"
          showClear={false}
          className="fp-search-suggest"
        />
        {filters.search && <button className="fp-search-x" onClick={() => onChange({ search: '' })} aria-label="Clear search"><AppIcon name="close" size={14} /></button>}
      </div>

      <div className="fp-sections">
        {filterConfig.map(cfg => renderFilter(cfg))}
      </div>
    </div>
  );

  return (
    <>
      {desktopVisible ? (
        <div className={`fp-sidebar-wrap ${desktopWrapperClassName} ${desktopCollapsed ? 'is-collapsed' : ''}`.trim()}>
          <aside className="fp-sidebar">{panelContent}</aside>
        </div>
      ) : null}
      {mobileOpen && (
        <div className="fp-mob-back" onClick={onMobileClose}>
          <div className="fp-mob-sheet" onClick={e => e.stopPropagation()}>
            <div className="fp-mob-handle"/>
            <div className="fp-mob-scroll">{panelContent}</div>
            <div className="fp-mob-footer">
              <button className="fp-mob-apply" onClick={onMobileClose}>
                Apply {totalActive > 0 ? `(${totalActive})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterPanel;